import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import EmptyGroups from '../../components/groups/EmptyGroups';
import GroupCard from '../../components/groups/GroupCard';
import GroupsHeader from '../../components/groups/GroupsHeader';
import { getPagedGroups } from '../../services/groupService';
import { GroupType, allowedGroupTypes } from '../../types/groups';
import type { GroupTab } from '../../types/groups';

const PAGE_SIZE = 10;
const GROUP_TYPE_TABS: GroupTab[] = ['All', ...allowedGroupTypes];

function GroupsSkeleton() {
  return (
    <div className="space-y-4" aria-label="Loading groups">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex animate-pulse flex-col gap-4 sm:flex-row">
            <div className="h-20 w-20 rounded-lg bg-gray-200" />
            <div className="flex-1 space-y-3">
              <div className="h-5 w-2/5 rounded bg-gray-200" />
              <div className="h-4 w-3/5 rounded bg-gray-100" />
              <div className="h-4 w-full rounded bg-gray-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GroupsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeGroupType, setActiveGroupType] = useState<GroupTab>('All');
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPageIndex(0);
    }, 500);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const handleGroupTypeChange = (groupType: GroupTab) => {
    setActiveGroupType(groupType);
    setPageIndex(0);
  };

  const start = pageIndex * PAGE_SIZE;
  const {
    data: pagedGroups,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['groups', 'paged', activeGroupType, start, PAGE_SIZE, debouncedSearch],
    queryFn: () =>
      getPagedGroups({
        start,
        length: PAGE_SIZE,
        orderColumn: 'CreatedAt',
        orderDirection: 'Descending',
        search: debouncedSearch,
        groupType: activeGroupType === 'All' ? undefined : activeGroupType,
      }),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const groups = pagedGroups?.data ?? [];
  const visibleGroups = groups
    .filter((group) => allowedGroupTypes.includes(group.groupType as GroupType))
    .filter((group) => activeGroupType === 'All' || group.groupType === activeGroupType);
  // The API response can contain groups from every type, so use the same
  // locally-filtered collection for the displayed range and count.
const recordsFiltered =
  pagedGroups?.recordsFiltered ??
  pagedGroups?.recordsTotal ??
  0;

const recordsTotal =
  pagedGroups?.recordsTotal ?? recordsFiltered;

const hasPreviousPage = pageIndex > 0;

// If current page returned 10 records,
// assume another page may exist.
const hasNextPage = visibleGroups.length === PAGE_SIZE;

const pageStart =
  visibleGroups.length === 0 ? 0 : start + 1;

const pageEnd =
  start + visibleGroups.length;

  return (
    <div className="space-y-6">
      <GroupsHeader searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      <div className="flex flex-wrap gap-4" role="radiogroup" aria-label="List Type">
        {GROUP_TYPE_TABS.map((groupType) => {
          const isActive = activeGroupType === groupType;

          return (
            <label
              key={groupType}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-sm font-semibold transition ${
                isActive
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <input
                type="radio"
                name="groupType"
                value={groupType}
                checked={isActive}
                onChange={() => handleGroupTypeChange(groupType)}
                //className="h-4 w-4 accent-blue-600"
                className="cursor-pointer"
              />
              <span>{groupType}</span>
            </label>
          );
        })}
      </div>

      {isLoading ? (
        <GroupsSkeleton />
      ) : isError ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center rounded-lg border border-gray-200 bg-white px-6 py-10 text-center shadow-sm">
          <h2 className="mb-2 text-xl font-bold text-gray-900">Unable to load groups.</h2>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 inline-flex h-10 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      ) : visibleGroups.length > 0 ? (
        <>
          <div className={`space-y-4 transition ${isFetching ? 'opacity-70' : 'opacity-100'}`}>
            {visibleGroups.map((group) => (
              <GroupCard key={group.groupId} group={group} />
            ))}
          </div>

          <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <p className="mb-0 text-sm text-gray-600">
              Showing {pageStart}-{pageEnd} of {recordsFiltered} groups
              {recordsTotal !== recordsFiltered ? ` (${recordsTotal} total)` : ''}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!hasPreviousPage || isFetching}
                onClick={() => setPageIndex((current) => Math.max(current - 1, 0))}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={!hasNextPage || isFetching}
                onClick={() => setPageIndex((current) => current + 1)}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <EmptyGroups />
      )}
    </div>
  );
}
